import React from 'react';
import { Analytics } from '@vercel/analytics/react';
import './ProjectDetail.css';

const Arrow = () => <span aria-hidden="true">↗</span>;

function ProjectDetail({ project, analyticsEnabled }) {
  return (
    <div className="detail-shell">
      <header className="detail-header">
        <a className="brand" href="/#top">
          <span className="brand-copy">Antoine Marchal<small>Solution Architect</small></span>
        </a>
        <a className="detail-back" href="/#work">← All projects</a>
      </header>

      <main>
        <section className="detail-hero">
          <div className="detail-hero-copy">
            <p className="eyebrow">{project.number} · {project.eyebrow}</p>
            <h1>{project.name}</h1>
            <p className="detail-tagline">{project.tagline}</p>
            <div className="tag-list">{project.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            <div className="detail-facts">
              {project.facts.map(([value, label]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
            </div>
          </div>
          <div className="detail-hero-visual">
            <div className="window-chrome"><span /><span /><span /><b>{project.slug}.local</b></div>
            <img src={project.image} alt={project.alt} style={{ objectPosition: project.imagePosition }} />
          </div>
        </section>

        <section className="detail-overview">
          <div>
            <p className="eyebrow">The product</p>
            <h2>Why I built it.</h2>
          </div>
          <div className="detail-narrative">
            <p className="detail-lead">{project.problem}</p>
            <div className="audience-note"><span>Designed for</span><p>{project.audience}</p></div>
          </div>
        </section>

        <section className="detail-tour">
          <div className="detail-section-heading">
            <p className="eyebrow">Inside the product</p>
            <h2>Main workflows.</h2>
          </div>
          <div className="tour-grid">
            {project.tour.map((item, index) => (
              <article key={item.title}>
                <div className="tour-crop">
                  <img
                    src={item.image || project.image}
                    alt={item.alt || ''}
                    style={{
                      objectPosition: item.position || 'center top',
                      transform: `scale(${item.zoom || 1})`,
                      transformOrigin: item.position || 'center',
                    }}
                  />
                </div>
                <div className="tour-caption"><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{item.title}</h3><p>{item.copy}</p></div></div>
              </article>
            ))}
          </div>
        </section>

        {project.more?.length > 0 && (
          <section className="detail-more">
            <div className="detail-section-heading">
              <p className="eyebrow">More of the product</p>
              <h2>Other parts of the product.</h2>
              <p className="section-intro">Additional views covering the administration, governance, and day-to-day features around the main workflows.</p>
            </div>
            <div className="more-grid">
              {project.more.map((item) => (
                <article key={item.title}>
                  <div className="more-crop"><img src={item.image} alt={item.alt || ''} loading="lazy" /></div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="detail-capabilities">
          <div className="detail-section-heading">
            <p className="eyebrow">Capabilities</p>
            <h2>What it can do.</h2>
          </div>
          <div className="capability-grid">
            {project.capabilities.map(([title, copy], index) => (
              <article key={title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
        </section>

        <section className="detail-difference">
          <div className="detail-section-heading">
            <p className="eyebrow">Why it is different</p>
            <h2>Key design choices.</h2>
          </div>
          <div className="difference-grid">
            {project.differentiators.map(([title, copy], index) => (
              <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>
            ))}
          </div>
        </section>

        <section className="detail-architecture">
          <div className="detail-section-heading">
            <p className="eyebrow">System architecture</p>
            <h2>How it is structured.</h2>
          </div>
          <div className="architecture-stack">
            {project.architecture.map(([title, copy], index) => (
              <article key={title}><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{title}</h3><p>{copy}</p></div></article>
            ))}
          </div>
        </section>

        <section className="detail-governance">
          <div className="detail-section-heading">
            <p className="eyebrow">Trust and governance</p>
            <h2>Access and operational controls.</h2>
          </div>
          <div className="governance-layout">
            <div className="governance-list">
              {project.governance.map(([title, copy]) => <article key={title}><h3>{title}</h3><p>{copy}</p></article>)}
            </div>
            <aside>
              <p className="eyebrow">Best suited to</p>
              <ul>{project.useCases.map((item) => <li key={item}>{item}</li>)}</ul>
            </aside>
          </div>
        </section>

        <section className="detail-workflow">
          <p className="eyebrow">Core workflow</p>
          <div className="workflow-line">
            {project.workflow.map((step, index) => <div key={step}><span>{index + 1}</span><strong>{step}</strong></div>)}
          </div>
        </section>

        <section className="detail-deployment">
          <div><p className="eyebrow">Deployment model</p><h2>How it is deployed.</h2></div>
          <div><p>{project.deployment}</p><p className="status-note">{project.status}</p></div>
        </section>

        <section className="detail-source">
          <p className="eyebrow">Public source</p>
          <h2>Explore {project.name}.</h2>
          <p>Source code is public. Please contact me before reusing this project. Questions, feedback, and reviews are welcome.</p>
          <div className="detail-source-actions">
            <a className="button button-light" href={project.repository} target="_blank" rel="noreferrer">View on GitHub <Arrow /></a>
            <a className="button button-light" href={`mailto:antoine.marchal@pm.me?subject=${encodeURIComponent(project.name + ' feedback or reuse')}`}>Get in touch <Arrow /></a>
          </div>
        </section>
      </main>

      <footer className="detail-footer"><a href="/#work">← Back to selected work</a><p>© {new Date().getFullYear()} Antoine Marchal</p></footer>
      {analyticsEnabled && <Analytics />}
    </div>
  );
}

export default ProjectDetail;
